import {
  type DiscountAvailabilityStrategy,
  firstTableDiscountAvailabilityStrategy,
} from "./discountAvailabilityStrategy";
import expandSessionsForFirebase from "./expandSessionsForFirebase";
import pickPreferredDinerAvailability from "./pickPreferredDinerAvailability";
import { type FirebaseAllAvailabilitySlot } from "../../firebase/allAvailabilityTypes";
import { type FirebaseAllAvailabilityById } from "../../model/firebaseAllAvailability";
import isInTime from "../../model/times/isInTime";
import { type Time } from "../../model/times/model";
import { type DiscountAvailabilityByDate } from "../../model/types";
import { timeToNumber } from "../slots/time";

export type DiscountAvailabilityFilterContext = {
  /** Sessions the Firebase availability was fetched under. */
  sessions: SessionType[];
  hasLastTable: boolean;
  timesStart: Time;
  timesEnd: Time;
  showAllDiscounts: boolean;
  people: number;
};

type RestaurantFirebaseAllAvailability = FirebaseAllAvailabilityById[number];

export const buildFilteredDiscountAvailabilityForRestaurant = (
  firebaseAllAvailability: RestaurantFirebaseAllAvailability | undefined,
  discountDatesToShow: readonly string[],
  context: DiscountAvailabilityFilterContext,
  strategy: DiscountAvailabilityStrategy = firstTableDiscountAvailabilityStrategy,
  /**
   * Written for days no requested session can supply. `null` when the venue
   * offers none of those sessions (resolved empty); `undefined` while a
   * supported session is still loading.
   */
  unresolvedPlaceholder: null | undefined = undefined,
): DiscountAvailabilityByDate | undefined => {
  if (!firebaseAllAvailability) {
    return undefined;
  }

  // Venue supports none of the active sessions: do not harvest leftover keys
  // from a prior First Table (or other mode) snapshot.
  if (unresolvedPlaceholder === null) {
    return discountDatesToShow.reduce(
      (slotsByDate: DiscountAvailabilityByDate, date) => {
        slotsByDate[date] = null;
        return slotsByDate;
      },
      {},
    );
  }

  const sessions = expandSessionsForFirebase(
    context.sessions,
    context.hasLastTable,
  );
  const sessionsAvailability = sessions
    .map((filterSession) => firebaseAllAvailability[filterSession])
    .filter((availabilityByDate) => !!availabilityByDate);

  return discountDatesToShow.reduce(
    (slotsByDate: DiscountAvailabilityByDate, date) => {
      const daySessionsAvailability = sessionsAvailability.map(
        (availabilityByDate) => availabilityByDate[date],
      );

      const doneLoadingAllSessions =
        daySessionsAvailability.length > 0 &&
        daySessionsAvailability.every((slots) => slots !== undefined);

      if (!doneLoadingAllSessions) {
        slotsByDate[date] =
          daySessionsAvailability.length === 0
            ? unresolvedPlaceholder
            : undefined;

        return slotsByDate;
      }

      const allSessionsEmpty = daySessionsAvailability.every(
        (slots) => slots === null,
      );

      if (allSessionsEmpty) {
        slotsByDate[date] = null;

        return slotsByDate;
      }

      const mergedDayAvailability = daySessionsAvailability.reduce(
        (daySlots: FirebaseAllAvailabilitySlot[], dayAvailability) => {
          if (dayAvailability !== null) {
            return daySlots.concat(dayAvailability);
          }

          return daySlots;
        },
        [],
      );

      const discountFilteredDayAvailability = context.showAllDiscounts
        ? mergedDayAvailability
        : mergedDayAvailability.filter((slot) => slot.discount === 0.5);

      slotsByDate[date] = discountFilteredDayAvailability.filter(
        (slot) =>
          strategy.includeSlot(slot) &&
          isInTime(slot.time, context.timesStart, context.timesEnd),
      );

      return slotsByDate;
    },
    {},
  );
};

export const sortDiscountAvailabilityByDate = (
  availabilityByDate: DiscountAvailabilityByDate,
  people: number,
  strategy: DiscountAvailabilityStrategy = firstTableDiscountAvailabilityStrategy,
): DiscountAvailabilityByDate => {
  const sortedAvailabilityByDate: DiscountAvailabilityByDate = {};

  for (const [date, dateAvailability] of Object.entries(availabilityByDate)) {
    sortedAvailabilityByDate[date] =
      dateAvailability !== null && dateAvailability !== undefined
        ? pickPreferredDinerAvailability(
            dateAvailability.slice(),
            people,
            strategy,
          ).sort(
            (slotA, slotB) =>
              timeToNumber(slotA?.time) - timeToNumber(slotB?.time),
          )
        : dateAvailability;
  }

  return sortedAvailabilityByDate;
};
